import React, { useCallback, useState } from 'react';
import { ChevronDown, Gift, HandCoins, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { C } from '@/design-system/dna';
import { brl, fmtDate, OPEN } from '../shared';
import { ParticipantShell, PayBadge, SelectAll, TableCell, TableRow, useParticipantFilter } from '../ParticipantTable';
import { PaymentDialog } from '../PaymentDialog';

const isOpen = (p) => OPEN.includes(p.status);

function Historico({ p, onReverse }) {
  return (
    <div className="space-y-1.5" data-testid={`evento-rec-hist-${p.student_id}`}>
      {p.payments.map((x, i) => (
        <div key={x.payment_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-white px-3 py-2 text-[12px]" data-testid={`evento-rec-pg-${x.payment_id}`}>
          <b>#{i + 1}</b><span className="tabular-nums font-bold">{brl(x.gross_amount)}</span><span>{fmtDate(x.paid_at)}</span><span>{x.method_label}{x.installments > 1 ? ` ${x.installments}x` : ''}</span>
          <span style={{ color: C.muted }}>{x.account_name || 'sem conta'}</span><span style={{ color: C.muted }}>taxa {brl(x.fee_amount)} · líquido {brl(x.net_amount)}</span>
          <span style={{ color: C.muted }}>por {x.user_name || '—'}</span>{x.notes && <span className="italic" style={{ color: C.muted }}>“{x.notes}”</span>}
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => onReverse(p, x)} data-testid={`evento-estornar-${x.payment_id}`}><Undo2 className="h-3.5 w-3.5" /> Estornar</Button>
        </div>
      ))}
    </div>
  );
}

export function Recebimentos({ data, setData, eventId }) {
  const parts = data.participants.filter((p) => p.participa);
  const { list, toolbar } = useParticipantFilter(parts);
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  const [targets, setTargets] = useState([]);
  const close = useCallback(() => setTargets([]), []);
  const batch = list.filter((p) => sel.includes(p.student_id) && isOpen(p));
  const setIsento = async (ids, isento) => {
    try { const { data: d } = await api.post(`/eventos/${eventId}/participantes/lote`, { student_ids: ids, isento }); setData(d); setSel([]); toast.success(isento ? 'Marcado como isento.' : 'Isenção removida.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha.'); }
  };
  const estornar = async (p, pm) => {
    const reason = window.prompt(`Motivo do estorno de ${brl(pm.gross_amount)} (${p.student_name}):`);
    if (!reason) return;
    try { const { data: d } = await api.post(`/eventos/${eventId}/pagamentos/${pm.payment_id}/estorno`, { reason }); setData(d); toast.success('Recebimento estornado.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao estornar.'); }
  };
  const rows = list.flatMap((p) => [
    <TableRow key={p.student_id} data-testid={`evento-rec-${p.student_id}`}>
      <TableCell><Checkbox checked={sel.includes(p.student_id)} onCheckedChange={() => setSel((s) => (s.includes(p.student_id) ? s.filter((i) => i !== p.student_id) : [...s, p.student_id]))} data-testid={`evento-sel-${p.student_id}`} /></TableCell>
      <TableCell><p className="font-semibold">{p.student_name}</p><p className="text-[11px]" style={{ color: C.muted }}>{p.class_name} · {p.responsible_name || '—'}</p></TableCell>
      <TableCell className="tabular-nums">{brl(p.valor_cobrado)}</TableCell>
      <TableCell className="tabular-nums" data-testid={`evento-pago-${p.student_id}`}>{brl(p.pago)}{p.fee > 0 && <span className="block text-[10.5px]" style={{ color: C.muted }}>taxa {brl(p.fee)}</span>}</TableCell>
      <TableCell className="tabular-nums font-bold" style={{ color: p.saldo > 0 ? C.orange : C.muted }} data-testid={`evento-saldo-${p.student_id}`}>{brl(p.saldo)}</TableCell>
      <TableCell>{p.forma || '—'}<span className="block text-[10.5px]" style={{ color: C.muted }}>{p.payments.length ? `${p.payments.length} recebimento(s) · ${fmtDate(p.data_pagamento)}` : ''}</span></TableCell>
      <TableCell><PayBadge p={p} /></TableCell>
      <TableCell className="whitespace-nowrap text-right">
        {isOpen(p) && <Button size="sm" onClick={() => setTargets([p])} data-testid={`evento-pagar-${p.student_id}`}><HandCoins className="h-4 w-4" /> {p.status === 'parcial' ? 'Receber saldo' : 'Receber'}</Button>}
        {p.payments.length > 0 && <Button size="sm" variant="ghost" onClick={() => setOpen(open === p.student_id ? null : p.student_id)} title="Recebimentos" data-testid={`evento-rec-toggle-${p.student_id}`}><ChevronDown className={`h-4 w-4 transition-transform ${open === p.student_id ? 'rotate-180' : ''}`} /></Button>}
        {!p.pago && <Button size="sm" variant="ghost" onClick={() => setIsento([p.student_id], p.status !== 'isento')} title={p.status === 'isento' ? 'Remover isenção' : 'Isentar'} data-testid={`evento-isentar-${p.student_id}`}><Gift className="h-4 w-4" /></Button>}
      </TableCell>
    </TableRow>,
    open === p.student_id && <TableRow key={`${p.student_id}-h`}><TableCell colSpan={8} className="bg-[#FBF9FD] p-3"><Historico p={p} onReverse={estornar} /></TableCell></TableRow>,
  ]).filter(Boolean);
  const actions = (
    <>
      {toolbar}
      <Button size="sm" disabled={!batch.length} onClick={() => setTargets(batch)} data-testid="evento-pagar-lote"><HandCoins className="h-4 w-4" /> Receber em lote ({batch.length})</Button>
      <Button size="sm" variant="outline" disabled={!sel.length} onClick={() => setIsento(sel, true)} data-testid="evento-isentar-lote"><Gift className="h-4 w-4" /> Isentar</Button>
    </>
  );
  return (
    <>
      <ParticipantShell title={`Recebimentos (${list.length})`} toolbar={actions} testid="evento-recebimentos-table" rows={rows}
        head={[<SelectAll key="s" list={list} sel={sel} setSel={setSel} />, 'Aluno', 'Valor', 'Pago', 'Saldo', 'Último recebimento', 'Status', '']}
        footer={`A receber ${brl(data.dashboard.a_receber)} · recebido ${brl(data.dashboard.receita_bruta)} · 1 obrigação por aluno, quantos recebimentos forem necessários (qualquer forma). Cada recebimento entra no Caixa/DRE/Lançamentos na hora.`} />
      <PaymentDialog eventId={eventId} targets={targets} onClose={close} onDone={(d) => { setData(d); setTargets([]); setSel([]); }} />
    </>
  );
}
